import axios from "axios";
import { API_URL } from "../constants";

export interface MedicalCheckFile {
  id: string;
  file: { id: string; name?: string; title?: string } | string;
}

export interface MedicalCheck {
  id: string;
  company: string;
  user: string;
  onboarding?: string | null;
  status: "pending" | "approved" | "superseded";
  validFrom?: number | null;
  validUntil?: number | null;
  approvedBy?: { id: string; name?: string; email?: string } | null;
  approvedAt?: number | null;
  createdBy?: { id: string; name?: string; email?: string } | null;
  createdAt?: number;
  files: MedicalCheckFile[];
  employee?: { id: string; name?: string; email?: string };
  canApproveMedical?: boolean;
  medicalApprovers?: string[];
}

export interface MedicalCheckEligibility {
  canInitiate: boolean;
  reason?: string;
  pendingCheckId?: string;
  latestApproved?: MedicalCheck | null;
  renewAvailableFrom?: number;
}

export interface MedicalChecksByUserResponse {
  checks: MedicalCheck[];
  eligibility: MedicalCheckEligibility;
  canApproveMedical: boolean;
  medicalApprovers: string[];
  canUploadMedical: boolean;
}

export interface MedicalCheckQueueResponse {
  pendingItems: MedicalCheck[];
  approvedItems: MedicalCheck[];
  /** @deprecated use approvedItems */
  approvedByMeItems?: MedicalCheck[];
  items: MedicalCheck[];
  count: number;
  canUploadMedical: boolean;
}

export async function fetchMedicalChecksByUser(
  companyId: string,
  userId: string
): Promise<MedicalChecksByUserResponse> {
  const res = await axios.get(
    `${API_URL}/${companyId}/users/${userId}/medical-checks`
  );
  return res.data;
}

export async function fetchMedicalCheckQueue(
  companyId: string
): Promise<MedicalCheckQueueResponse> {
  const res = await axios.get(`${API_URL}/${companyId}/medical-checks/queue`);
  return res.data;
}

export async function createMedicalCheck(
  companyId: string,
  userId: string
): Promise<MedicalCheck> {
  const res = await axios.post(
    `${API_URL}/${companyId}/users/${userId}/medical-checks`
  );
  return res.data;
}

export async function addMedicalCheckFile(
  companyId: string,
  checkId: string,
  fileId: string
): Promise<void> {
  await axios.post(`${API_URL}/${companyId}/medical-checks/${checkId}/files`, {
    fileId,
  });
}

export async function deleteMedicalCheckFile(
  companyId: string,
  checkId: string,
  fileId: string
): Promise<void> {
  await axios.delete(
    `${API_URL}/${companyId}/medical-checks/${checkId}/files/${fileId}`
  );
}

export async function approveMedicalCheck(
  companyId: string,
  checkId: string,
  data: { validFrom: number; validUntil: number }
): Promise<MedicalCheck> {
  const res = await axios.post(
    `${API_URL}/${companyId}/medical-checks/${checkId}/approve`,
    data
  );
  return res.data;
}

export async function reopenMedicalCheck(
  companyId: string,
  checkId: string
): Promise<MedicalCheck> {
  const res = await axios.post(
    `${API_URL}/${companyId}/medical-checks/${checkId}/reopen`
  );
  return res.data;
}

export async function deleteMedicalCheck(
  companyId: string,
  checkId: string
): Promise<void> {
  await axios.delete(`${API_URL}/${companyId}/medical-checks/${checkId}`);
}
